import React, { useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "../../../utils/classNames";
import "./index.scss";

const INTERACTIVE_SELECTOR = "[data-structured-list-row-interactive='true']";

export default function StructuredListRow({
  className = "",
  identity,
  identityClassName = "",
  snapshot = null,
  snapshotClassName = "",
  connection = null,
  connectionClassName = "",
  actions = null,
  actionsClassName = "",
  onActivate = undefined,
  activateAriaLabel = undefined,
}) {
  const isClickable = typeof onActivate === "function";

  const stopInteractiveEvent = useCallback((event) => {
    event.stopPropagation();
    if (typeof event.nativeEvent?.stopImmediatePropagation === "function") {
      event.nativeEvent.stopImmediatePropagation();
    }
  }, []);

  const handleRowActivate = useCallback((event) => {
    if (!isClickable) {
      return;
    }

    const target = event?.target;
    if (target instanceof Element && target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }

    onActivate();
  }, [isClickable, onActivate]);

  const handleRowKeyDown = useCallback((event) => {
    if (!isClickable || event.defaultPrevented || event.target !== event.currentTarget) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  }, [isClickable, onActivate]);

  return (
    <article
      className={classNames(
        "structured-list-row",
        isClickable && "structured-list-row--interactive",
        className
      )}
      onClick={isClickable ? handleRowActivate : undefined}
      onKeyDown={isClickable ? handleRowKeyDown : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? activateAriaLabel : undefined}
    >
      <div className={classNames("structured-list-row__identity", identityClassName)}>
        {identity}
      </div>
      {snapshot ? (
        <div className={classNames("structured-list-row__snapshot", snapshotClassName)}>
          {snapshot}
        </div>
      ) : null}
      {connection ? (
        <div className={classNames("structured-list-row__connection", connectionClassName)}>
          {connection}
        </div>
      ) : null}
      {actions ? (
        <div
          className={classNames("structured-list-row__actions", actionsClassName)}
          data-structured-list-row-interactive="true"
          onClick={stopInteractiveEvent}
          onPointerDown={stopInteractiveEvent}
        >
          {actions}
        </div>
      ) : null}
    </article>
  );
}

StructuredListRow.propTypes = {
  className: PropTypes.string,
  identity: PropTypes.node.isRequired,
  identityClassName: PropTypes.string,
  snapshot: PropTypes.node,
  snapshotClassName: PropTypes.string,
  connection: PropTypes.node,
  connectionClassName: PropTypes.string,
  actions: PropTypes.node,
  actionsClassName: PropTypes.string,
  onActivate: PropTypes.func,
  activateAriaLabel: PropTypes.string,
};
